import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { verifyEmail, resendVerificationEmail } from './index';
import Spinner from '../components/Spinner';

const VerifyEmail = () => {
  const { token } = useParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');

  // Resend Logic State
  const [email, setEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');

  const lastAttemptedToken = useRef<string>('');

  useEffect(() => {
    const verify = async () => {
      if (!token) return;
      // Prevent double execution in React Strict Mode
      if (lastAttemptedToken.current === token) return;
      lastAttemptedToken.current = token;

      try {
        await verifyEmail(token);
        setStatus('success');
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.message || 'Verification failed. Link might be expired.');
      }
    };

    verify();
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendStatus('sending');
    try {
      await resendVerificationEmail(email);
      setResendStatus('success');
      setResendMessage('Verification link sent! Please check your email.');
    } catch (err: any) {
      setResendStatus('error');
      setResendMessage(err.response?.data?.message || 'Failed to resend link.');
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-20">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
          
          {status === 'verifying' && (
            <div>
              <h2 className="text-2xl font-serif font-bold text-primary mb-4">Verifying...</h2>
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
            </div>
          )}

          {status === 'success' && (
            <div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-serif font-bold text-primary mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-6">Your account has been successfully activated.</p>
              <Link to="/login" className="inline-block w-full py-2 px-4 bg-primary text-white rounded-md hover:bg-green-800 transition">
                Login Now
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-serif font-bold text-red-600 mb-2">Verification Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              
              <div className="border-t border-gray-100 pt-6 mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Resend Verification Link</h3>
                <p className="text-sm text-gray-500 mb-4">Enter your email address to receive a new link.</p>
                
                {resendStatus === 'success' ? (
                  <div className="bg-green-50 text-green-700 p-3 rounded text-sm mb-4">
                    {resendMessage}
                  </div>
                ) : (
                  <form onSubmit={handleResend} className="space-y-3">
                    <input 
                      type="email" 
                      required
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
                    />
                    {resendStatus === 'error' && <p className="text-xs text-red-600">{resendMessage}</p>}
                    <button 
                      type="submit" 
                      disabled={resendStatus === 'sending'}
                      className="w-full bg-primary text-white py-2 rounded-md hover:bg-green-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {resendStatus === 'sending' ? <><Spinner /> Sending...</> : 'Resend Link'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;